/**
 * AddPage Component
 * Main page for the "Add Documentation" feature.
 *
 * Flow:
 * 1. Record audio
 * 2. Upload to S3
 * 3. Select or create project (REQUIRED)
 * 4. Template selection (via TemplatePickerModal)
 * 5. Submit async job
 * 6. Reset to step 1 for a new recording
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Sparkles,
  AlertCircle,
  Mic,
  Image,
  GitBranch,
  ClipboardList,
  LayoutDashboard,
  FolderOpen,
  Plus,
  Check,
  ArrowRight,
  Loader2,
  RotateCcw,
  FileUp,
  ClipboardPaste,
  AlertTriangle,
  Clock,
  Trash2,
  Play,
  Search,
} from 'lucide-react';
import { PageLayout } from '@/components';
import { Button, UnauthenticatedPage, TabsList, Tab, AppPageHeader } from '@/components/ui';
import { Chrome } from '@/components/layout';
import { TemplatePickerModal } from '@/components/TemplatePickerModal';
import { useAuthContext } from '@/contexts/AuthContext';
import { addApi } from '@/services/addApi';
import { useTemplates } from '@/hooks/useTemplates';
import { useProjectConfig } from '@/hooks/useProjectConfig';
import { projectsApi } from '@/services/projectsApi';
import { usePendingJobs } from '@/contexts/PendingJobsContext';
import { useAdd } from '@/contexts/AddContext';
import { useInterruptedSessions } from '@/hooks/useInterruptedSessions';
import { captureError } from '@/lib/sentry';
import { errorMessages } from '@/lib/errors';
import { getUserIdentityId } from '@/lib/awsClients';
import { createLogger } from '@/lib/logger';
import { resetCorrelationId } from '@/lib/correlationId';
import type { CreateStep, InputMethod } from '@/lib/types';
import type { ProjectConfig } from '@/lib/types';
import type { TemplateMetadata } from '@/services/templatesApi';

import { FileUploader } from './FileUploader';
import { AudioRecorder } from './AudioRecorder';
import { TextInputPanel } from './TextInputPanel';
import { DocumentUploadPanel } from './DocumentUploadPanel';
import { CapturedItemsPanel, type AudioRecording, type CapturedStreamingTranscript } from './CapturedItemsPanel';
import { PhotoCapturePanel } from './PhotoCapturePanel';
import { MyDraftsPanel } from './MyDraftsPanel';
import { ProgressSteps } from './ProgressSteps';
import { useAddWorkflowState } from './useAddWorkflowState';
import { useDrafts } from '@/hooks/useDrafts';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { useBlobUploader } from '@/hooks/useBlobUploader';
import type { CapturedPhoto, RecordingNote } from '@/services/indexedDBSessionService';

const log = createLogger('Add');

// Project type for display metadata
type ProjectType =
  | 'web-application'
  | 'mobile-app'
  | 'api-backend'
  | 'data-platform'
  | 'infrastructure'
  | 'other';

// Metadata for project types (icons, colors)
const projectTypeMetadata: Record<ProjectType, { icon: React.ElementType; color: string; description: string }> = {
  'web-application': {
    icon: LayoutDashboard,
    color: 'blue',
    description: 'Web application',
  },
  'mobile-app': {
    icon: FileText,
    color: 'purple',
    description: 'Mobile app',
  },
  'api-backend': {
    icon: GitBranch,
    color: 'emerald',
    description: 'API & backend',
  },
  'data-platform': {
    icon: ClipboardList,
    color: 'amber',
    description: 'Data platform',
  },
  'infrastructure': {
    icon: LayoutDashboard,
    color: 'slate',
    description: 'Infrastructure',
  },
  'other': {
    icon: FolderOpen,
    color: 'indigo',
    description: 'Project',
  },
};

export const AddPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { templates: userTemplates, refresh: refreshTemplates } = useTemplates();
  const { projects, createProject, updateProject, isLoading: projectsLoading, refresh: refreshProjects } = useProjectConfig();
  const { addJob } = usePendingJobs();
  const { recordedBlob, clearRecordedBlob, transcriptionMode, streaming, photos, notes, addNote, removeNote, removePhoto, duration, validateBeforeSubmit, restorePhotos, restoreNotes } = useAdd();
  const { drafts, isLoading: draftsLoading, isSaving: draftSaving, hasAttemptedLoad: draftsHasAttemptedLoad, loadDrafts, saveDraft, restoreDraft, deleteDraft } = useDrafts();
  const draftsEnabled = isFeatureEnabled('drafts');

  // Start a fresh correlation ID for each Create flow
  useEffect(() => { resetCorrelationId(); }, []);

  // Track whether a streaming recording has been "captured" to the items list.
  // Duration is snapshotted at capture time so it doesn't drift.
  const [streamingCaptured, setStreamingCaptured] = useState(false);
  const [capturedStreamingDuration, setCapturedStreamingDuration] = useState(0);

  // Restored draft data that doesn't map to CreateContext
  const [restoredStreamingTranscripts, setRestoredStreamingTranscripts] = useState<Array<{ id: string; transcript: string; duration: number }>>([]);
  const [restoredAudioDuration, setRestoredAudioDuration] = useState(0);
  const {
    interruptedRecordings,
    discardRecording: discardInterruptedRecording,
    previewSessionId,
    previewBlobUrl,
    previewLoading,
    previewRecording: previewInterruptedRecording,
    stopPreview,
  } = useInterruptedSessions();

  // Background blob uploader — uploads audio/photos to S3 immediately on capture
  const { uploads, uploadBlob, removeUpload, clearUploads, anyUploading, anyFailed } = useBlobUploader();
  const uploadedPhotoIdsRef = useRef<Set<string>>(new Set());
  const uploadedNoteIdsRef = useRef<Set<string>>(new Set());

  // Step management for async flow
  // capture -> project -> template -> save
  const [step, setStep] = useState<CreateStep>('capture');

  // Input method selection
  const [inputMethod, setInputMethod] = useState<InputMethod>('voice');
  const [note, setNote] = useState<string>('');

  // Data state
  const [audioFile, setAudioFile] = useState<Blob | null>(null);
  const [uploadedAudioFile, setUploadedAudioFile] = useState<File | Blob | null>(null); // Temporary state for uploaded audio before capture
  const [uploadedS3Key, setUploadedS3Key] = useState<string>('');
  const [uploadId, setUploadId] = useState<string>('');
  const [selectedTemplates, setSelectedTemplates] = useState<TemplateMetadata[]>([]);

  // Optimistic template cache — templates added this session that the GSI may not yet return
  const [recentTemplates, setRecentTemplates] = useState<TemplateMetadata[]>([]);

  // Templates fetched directly for the selected project (strongly consistent via batch_get_item)
  const [fetchedProjectTemplates, setFetchedProjectTemplates] = useState<TemplateMetadata[]>([]);
  const [projectTemplatesLoading, setProjectTemplatesLoading] = useState(false);

  // Project selection state
  const [selectedProject, setSelectedProject] = useState<ProjectConfig | null>(null);
  const [projectSearchQuery, setProjectSearchQuery] = useState<string>('');
  const [newProjectName, setNewProjectName] = useState<string>('');
  const [newProjectType, setNewProjectType] = useState<ProjectType>('other');

  // Recording language (auto-detected from browser/i18n settings)
  const recordingLanguage = useMemo(() => {
    // Map i18n language codes to AWS Transcribe language codes
    const languageMap: Record<string, string> = {
      'en': 'en-US',
      'en-GB': 'en-US',
      'en-US': 'en-US',
      'it': 'it-IT',
      'it-IT': 'it-IT',
      'es': 'es-ES',
      'es-ES': 'es-ES',
    };

    const browserLang = i18n.language || navigator.language || 'en';
    const baseLang = browserLang.split('-')[0];

    return languageMap[browserLang] || languageMap[baseLang] || 'en-US';
  }, [i18n.language]);

  // UI state
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Derived: audio vs text input category
  const inputCategory = (inputMethod === 'voice' || inputMethod === 'upload-recording') ? 'audio' : 'text';
  const isStreamingMode = transcriptionMode === 'streaming' && inputMethod === 'voice';
  const hasContent = streamingCaptured
    ? true
    : isStreamingMode
      ? !!(streaming.finalizedTranscript || streaming.sessionId)
      : inputCategory === 'audio'
        ? !!audioFile
        : inputMethod === 'text'
          ? notes.length > 0
          : !!note.trim();

  // Templates filtered to the selected project's assigned templateIds
  // Uses fetchedProjectTemplates (from batch_get_item — strongly consistent) as primary source,
  // merged with userTemplates (from GSI query) and recentTemplates (optimistic cache).
  const projectTemplates = useMemo((): TemplateMetadata[] => {
    if (!selectedProject?.templateIds?.length) return [];
    // Start with project-specific fetch (authoritative, strongly consistent)
    const allTemplates = [...fetchedProjectTemplates];
    // Merge in user templates from GSI query (may have additional metadata)
    for (const ut of (userTemplates || [])) {
      if (!allTemplates.some(t => t.id === ut.id)) {
        allTemplates.push(ut);
      }
    }
    // Merge in recently-added templates that neither source may return yet
    for (const rt of recentTemplates) {
      if (!allTemplates.some(t => t.id === rt.id)) {
        allTemplates.push(rt);
      }
    }
    return allTemplates.filter(t => selectedProject.templateIds!.includes(t.id));
  }, [fetchedProjectTemplates, userTemplates, recentTemplates, selectedProject]);

  // Workflow state persistence - allows resuming after navigating away
  const { clearState: clearWorkflowState } = useAddWorkflowState({
    step,
    uploadedS3Key,
    uploadId,
    inputMethod,
    note,
    selectedProject,
    selectedTemplate: selectedTemplates[0] || null, // Pass first template for compatibility
    setStep,
    setUploadedS3Key,
    setUploadId,
    setInputMethod,
    setNote,
    setSelectedProject,
    setSelectedTemplate: (template: TemplateMetadata | null) => {
      setSelectedTemplates(template ? [template] : []);
    },
    projects,
    templates: projectTemplates,
    sessionId: streaming.sessionId,
    transcriptionMode,
  });

  // Load interrupted streaming sessions on mount
  useEffect(() => {
    if (isAuthenticated && isStreamingMode) {
      streaming.loadInterruptedSessions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Load drafts on mount (guard on authLoading to avoid firing before API auth is ready)
  useEffect(() => {
    if (draftsEnabled && isAuthenticated && !authLoading) {
      loadDrafts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftsEnabled, isAuthenticated, authLoading]);

  // Refresh project list when entering the project step,
  // and template list when entering the template step
  useEffect(() => {
    if (!isAuthenticated) return;
    if (step === 'project') {
      refreshProjects();
    } else if (step === 'template') {
      refreshTemplates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isAuthenticated]);

  // Fetch project-specific templates via batch_get_item (strongly consistent)
  // when a project is selected. This is more reliable than filtering the GSI-based
  // user templates list, which uses eventually consistent reads.
  useEffect(() => {
    if (!selectedProject?.id || !isAuthenticated) {
      setFetchedProjectTemplates([]);
      return;
    }
    if (!selectedProject.templateIds?.length) {
      setFetchedProjectTemplates([]);
      return;
    }
    setProjectTemplatesLoading(true);
    projectsApi.getProjectTemplates(selectedProject.id)
      .then(templates => setFetchedProjectTemplates(templates))
      .catch(err => log.warn('Failed to fetch project templates', { error: err instanceof Error ? err.message : String(err) }))
      .finally(() => setProjectTemplatesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, isAuthenticated]);

  // Watch for completed recording from context — store blob locally
  useEffect(() => {
    if (recordedBlob) {
      setAudioFile(recordedBlob.blob);
      setRestoredAudioDuration(0); // Clear restored duration — new recording uses live duration
      clearRecordedBlob();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordedBlob]);

  // Upload audio to S3 immediately when captured
  useEffect(() => {
    if (audioFile && !uploads.has('audio')) {
      const ct = audioFile.type || 'audio/mp4';
      const ext = ct.includes('webm') ? 'webm' : 'mp4';
      uploadBlob('audio', audioFile, `recording.${ext}`, ct).then((entry) => {
        if (entry) {
          setUploadedS3Key(entry.s3Key);
          setUploadId(entry.uploadId);
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioFile]);

  // Upload photos to S3 immediately when captured
  useEffect(() => {
    for (const photo of photos) {
      if (!uploadedPhotoIdsRef.current.has(photo.id)) {
        uploadedPhotoIdsRef.current.add(photo.id);
        uploadBlob(photo.id, photo.blob, `photo-${photo.id}.jpg`, 'image/jpeg');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  // Upload notes to S3 immediately when captured
  useEffect(() => {
    for (const n of notes) {
      if (!uploadedNoteIdsRef.current.has(n.id)) {
        uploadedNoteIdsRef.current.add(n.id);
        const blob = new Blob([n.text], { type: 'text/plain' });
        uploadBlob(n.id, blob, `note-${n.id}.txt`, 'text/plain');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes]);

  // Handle streaming capture (user clicked Capture in AudioRecorder during streaming mode)
  // Instead of jumping to step 2, add the streaming recording to the Captured Items list
  const handleStreamingCapture = useCallback(() => {
    setCapturedStreamingDuration(streaming.duration);
    setStreamingCaptured(true);
    toast.success(t('create.recordingCaptured'));
  }, [streaming.duration, t]);

  // Handle discarding an interrupted batch recording
  const handleDiscardInterruptedRecording = useCallback(async (sessionId: string) => {
    try {
      await discardInterruptedRecording(sessionId);
      toast.success(t('create.recordingDiscarded'));
    } catch (err) {
      toast.error(t('create.discardFailed'));
    }
  }, [discardInterruptedRecording, t]);

  // Handle file selection (from upload - stores in temporary state)
  const handleFileSelected = useCallback((file: File | Blob) => {
    setUploadedAudioFile(file);
    setError(null);
  }, []);

  // Handle capturing uploaded audio file to captured items
  const handleCaptureAudio = useCallback(() => {
    if (uploadedAudioFile) {
      setAudioFile(uploadedAudioFile);
      setUploadedAudioFile(null);
      toast.success(t('create.audioCaptured'));
    }
  }, [uploadedAudioFile, t]);

  // Handle capturing uploaded document to captured items
  const handleCaptureDocument = useCallback(() => {
    if (note.trim()) {
      toast.success(t('create.documentCaptured', { fileName: 'document' }));
      setNote(''); // Clear the document upload area
    }
  }, [note, t]);

  // Handle template selection toggle (multi-select)
  const handleTemplateSelect = useCallback((template: TemplateMetadata) => {
    setSelectedTemplates(prev => {
      const isSelected = prev.some(t => t.id === template.id);
      if (isSelected) {
        // Remove from selection
        return prev.filter(t => t.id !== template.id);
      } else {
        // Add to selection
        return [...prev, template];
      }
    });

    // Auto-assign template to project if not already assigned
    if (selectedProject) {
      const currentTemplateIds = selectedProject.templateIds || [];
      if (!currentTemplateIds.includes(template.id)) {
        const updatedTemplateIds = [...currentTemplateIds, template.id];
        updateProject(selectedProject.id, { templateIds: updatedTemplateIds });
        setSelectedProject({
          ...selectedProject,
          templateIds: updatedTemplateIds,
        });
      }
    }
  }, [selectedProject, updateProject]);

  // Handle submitting multiple async jobs (one per selected template)
  const handleSubmitMultipleJobs = useCallback(async () => {
    if (!hasContent || !selectedProject || selectedTemplates.length === 0) return;

    // Block submission when offline
    const submitError = validateBeforeSubmit();
    if (submitError) {
      toast.error(t('create.noInternetConnection'));
      return;
    }

    // Ensure at least one template is selected
    if (selectedTemplates.length === 0) {
      setError(t('create.selectTemplateError'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Get the user's Identity Pool ID for consistent document storage
      const identityId = await getUserIdentityId();

      const jobIds: string[] = [];

      // Use pre-uploaded photo S3 keys from background uploads
      let photoAttachments: Array<{ type: 'photo'; s3Key: string; timestamp: number; index: number }> = [];
      if (photos.length > 0) {
        photoAttachments = photos
          .map((photo, index) => {
            const upload = uploads.get(photo.id);
            if (!upload || upload.status !== 'uploaded') return null;
            return {
              type: 'photo' as const,
              s3Key: upload.s3Key,
              timestamp: photo.timestamp,
              index,
            };
          })
          .filter((a): a is NonNullable<typeof a> => a !== null);
      }

      // Build notes attachments array — use pre-uploaded S3 keys from background uploads
      const noteAttachments = notes
        .map((n, index) => {
          const upload = uploads.get(n.id);
          if (!upload || upload.status !== 'uploaded') return null;
          return {
            type: 'note' as const,
            s3Key: upload.s3Key,
            timestamp: n.timestamp,
            index: photoAttachments.length + index,
          };
        })
        .filter((a): a is NonNullable<typeof a> => a !== null);

      // Combine all attachments
      const attachments = [...photoAttachments, ...noteAttachments];

      // Submit a job for each selected template
      for (const tpl of selectedTemplates) {
        const outputFormat = tpl.outputFormat || 'meeting-notes';
        let jobId: string | null = null;

        if (isStreamingMode && (streamingCaptured || restoredStreamingTranscripts.length > 0)) {
          // Captured/restored streaming path: transcript already available, submit as text
          const streamingTexts: string[] = [];
          if (streamingCaptured && streaming.finalizedTranscript) {
            streamingTexts.push(streaming.finalizedTranscript);
          }
          for (const rst of restoredStreamingTranscripts) {
            streamingTexts.push(rst.transcript);
          }
          const response = await addApi.submitAsyncJob({
            textContent: streamingTexts.join('\n\n'),
            skipTranscription: true,
            projectId: selectedProject.id,
            projectName: selectedProject.projectName,
            templateId: tpl.id,
            templateName: tpl.name,
            outputFormat,
            identityId,
            languageCode: recordingLanguage,
            ...(attachments.length > 0 && { attachments }),
          });
          jobId = response.jobId;
        } else if (isStreamingMode && streaming.sessionId) {
          // Live streaming path: stop session via WebSocket → backend assembles transcript → SQS
          // Note: We can only stop streaming once, so only process first template via streaming
          if (jobIds.length === 0) {
            jobId = await streaming.stopStreaming({
              projectId: selectedProject.id,
              projectName: selectedProject.projectName,
              templateId: tpl.id,
              templateName: tpl.name,
              outputFormat,
              identityId,
            });
          } else {
            // For additional templates, we'd need to use the transcript from the first job
            // This is a limitation of the current streaming architecture
            continue;
          }

          if (!jobId) {
            throw new Error('Failed to get job ID from streaming session');
          }
        } else if (isStreamingMode && !streaming.sessionId) {
          // No captured transcript and no active session — truly lost
          throw new Error(t('create.streamingSessionLost'));
        } else if (inputCategory === 'audio') {
          // Batch audio path: submit S3 key for transcription + documentation
          const response = await addApi.submitAsyncJob({
            uploadId,
            s3Key: uploadedS3Key,
            projectId: selectedProject.id,
            projectName: selectedProject.projectName,
            templateId: tpl.id,
            templateName: tpl.name,
            outputFormat,
            identityId,
            languageCode: recordingLanguage,
            ...(attachments.length > 0 && { attachments }),
          });
          jobId = response.jobId;
        } else {
          // Text path: submit text content (skips transcription)
          // For text input, notes are already uploaded to S3 — use the first note's S3 key as primary content
          // For upload-document, upload the pasted document text to S3 first
          let textS3Key: string | undefined;
          let textContent: string | undefined;

          if (inputMethod === 'text' && notes.length > 0) {
            // Notes already uploaded via background useEffect — reference via attachments
            textContent = notes.map(n => n.text).join('\n\n');
          } else if (note) {
            // Pasted document: upload to S3 now if not already uploaded
            const docId = `document-${Date.now()}`;
            const blob = new Blob([note], { type: 'text/plain' });
            const uploadResponse = await addApi.getUploadUrl(`${docId}.txt`, 'text/plain');
            await addApi.uploadMedia(uploadResponse.presignedUrl, blob, 'text/plain');
            textS3Key = uploadResponse.s3Key;
          }

          const response = await addApi.submitAsyncJob({
            textContent,
            ...(textS3Key && { s3Key: textS3Key }),
            skipTranscription: true,
            projectId: selectedProject.id,
            projectName: selectedProject.projectName,
            templateId: tpl.id,
            templateName: tpl.name,
            outputFormat,
            identityId,
            languageCode: recordingLanguage,
            ...(noteAttachments.length > 0 && { attachments: noteAttachments }),
          });
          jobId = response.jobId;
        }

        if (jobId) {
          jobIds.push(jobId);

          // Add job to pending jobs context for tracking
          addJob({
            jobId,
            status: 'pending',
            statusMessage: 'Waiting to process...',
            projectId: selectedProject.id,
            projectName: selectedProject.projectName,
            templateId: tpl.id,
            templateName: tpl.name,
            outputFormat,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
      }

      // Show success toast
      const toastTitle = isStreamingMode
        ? t('create.recordingSubmitted')
        : inputCategory === 'audio' ? t('create.recordingSubmitted') : t('create.documentSubmitted');
      const description = selectedTemplates.length > 1
        ? t('create.multipleDocumentsSubmitted', { count: selectedTemplates.length })
        : t('create.submittedDescription');

      toast.success(toastTitle, {
        description,
        duration: 5000,
        action: {
          label: t('create.viewProject'),
          onClick: () => navigate(`/projects?project=${selectedProject.id}`),
        },
      });

      // Clear workflow state and reset to upload step for a new recording
      clearWorkflowState();
      setStep('capture');
      setAudioFile(null);
      setUploadedS3Key('');
      setUploadId('');
      setNote('');
      setSelectedProject(null);
      setSelectedTemplates([]);
      setRecentTemplates([]);
      setFetchedProjectTemplates([]);
      setError(null);
      setShowTemplatePicker(false);
      setStreamingCaptured(false);
      setCapturedStreamingDuration(0);
      clearRecordedBlob();
      // Clear CreateContext items and restored draft data
      restorePhotos([]);
      restoreNotes([]);
      setRestoredStreamingTranscripts([]);
      setRestoredAudioDuration(0);
      // Clear background uploads
      clearUploads();
      uploadedPhotoIdsRef.current.clear();
      uploadedNoteIdsRef.current.clear();

    } catch (err) {
      const message = errorMessages.submitJob(err);
      setError(message);
      toast.error(t('create.submissionFailed'), {
        description: message,
      });
      captureError(err as Error, { component: 'AddPage', action: 'handleSubmitMultipleJobs' });
    } finally {
      setIsSubmitting(false);
    }
  }, [hasContent, inputCategory, isStreamingMode, streaming, streamingCaptured, restoredStreamingTranscripts, uploadedS3Key, uploadId, note, selectedProject, selectedTemplates, addJob, clearWorkflowState, clearRecordedBlob, clearUploads, uploads, recordingLanguage, t, navigate, inputMethod, notes, photos, restoreNotes, restorePhotos, validateBeforeSubmit]);

  // Add a template to the selected project (optimistic: shows immediately without waiting for GSI)
  const addTemplateToProject = useCallback((template: TemplateMetadata) => {
    if (!selectedProject) return;
    const currentIds = selectedProject.templateIds || [];
    if (currentIds.includes(template.id)) return;
    const updatedIds = [...currentIds, template.id];
    updateProject(selectedProject.id, { templateIds: updatedIds });
    setSelectedProject({ ...selectedProject, templateIds: updatedIds });
    setRecentTemplates(prev => prev.some(t => t.id === template.id) ? prev : [...prev, template]);
    // Also add to fetchedProjectTemplates so it shows immediately
    setFetchedProjectTemplates(prev => prev.some(t => t.id === template.id) ? prev : [...prev, template]);
    refreshTemplates();
  }, [selectedProject, updateProject, refreshTemplates]);

  // Reset to start over
  const handleStartOver = useCallback(() => {
    // Discard streaming session if active
    if (isStreamingMode && streaming.sessionStatus !== 'idle') {
      streaming.discardStreaming();
    }
    clearWorkflowState();
    setStep('capture');
    setInputMethod('voice');
    setNote('');
    setAudioFile(null);
    setUploadedS3Key('');
    setUploadId('');
    setSelectedTemplates([]);
    setSelectedProject(null);
    setProjectSearchQuery('');
    setNewProjectName('');
    setNewProjectType('other');
    setError(null);
    setIsSubmitting(false);
    setShowTemplatePicker(false);
    setStreamingCaptured(false);
    setCapturedStreamingDuration(0);
    // Clear CreateContext captured items
    restorePhotos([]);
    restoreNotes([]);
    // Clear restored draft data
    setRestoredStreamingTranscripts([]);
    setRestoredAudioDuration(0);
    // Clear background uploads
    clearUploads();
    uploadedPhotoIdsRef.current.clear();
    uploadedNoteIdsRef.current.clear();
    // Clear optimistic template cache
    setRecentTemplates([]);
    setFetchedProjectTemplates([]);
  }, [clearWorkflowState, isStreamingMode, streaming, restorePhotos, restoreNotes, clearUploads]);

  // Handle create new project (Generic Note auto-assigned by backend)
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const handleCreateProject = useCallback(async (nameOverride?: string) => {
    const name = (nameOverride || newProjectName).trim();
    if (!name) return;

    setProjectSearchQuery('');
    setNewProjectName('');
    setIsCreatingProject(true);

    try {
      // Await the server response so we get the real project ID and auto-assigned templateIds
      const serverProject = await createProject({
        projectName: name,
        type: newProjectType,
      });

      setSelectedProject(serverProject);

      // Refresh templates so the auto-copied Generic Note is available
      await refreshTemplates();

      setStep('template');
    } catch (err) {
      toast.error(t('create.failedToCreateProject'));
      log.error('Failed to create project', err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsCreatingProject(false);
    }
  }, [newProjectName, newProjectType, createProject, refreshTemplates, t]);

  // Handle project selection and advance to template step
  const handleProjectSelect = useCallback((project: ProjectConfig) => {
    setSelectedProject(project);
    setStep('template');
  }, []);

  // --- Draft handlers ---
  const [isRestoring, setIsRestoring] = useState(false);

  const handleSaveDraft = useCallback(async () => {
    try {
      // Build items payload for the draft API (array-based format)
      const draftItems: {
        audios: Array<{ filename: string; contentType: string; duration: number }>;
        photos: Array<{ filename: string; contentType: string; timestamp: number }>;
        notes: Array<{ id: string; text: string; timestamp: number }>;
        streamingTranscripts: Array<{ transcript: string; duration: number }>;
      } = {
        audios: [],
        photos: photos.map((p, i) => ({
          filename: `photo-${String(i + 1).padStart(3, '0')}.jpg`,
          contentType: 'image/jpeg',
          timestamp: p.timestamp,
        })),
        notes: notes.map(n => ({ id: n.id, text: n.text, timestamp: n.timestamp })),
        streamingTranscripts: [],
      };

      // Add audio metadata if we have a local audio blob
      if (audioFile) {
        const contentType = audioFile.type || 'audio/webm';
        const extension = contentType.includes('webm') ? 'webm' : 'mp4';
        draftItems.audios.push({
          filename: `audio.${extension}`,
          contentType,
          duration: duration,
        });
      }

      // Add streaming transcript if captured
      if (streamingCaptured && streaming.finalizedTranscript) {
        draftItems.streamingTranscripts.push({
          transcript: streaming.finalizedTranscript,
          duration: capturedStreamingDuration,
        });
      }

      const photoBlobs = photos.map(p => ({ blob: p.blob, timestamp: p.timestamp }));
      const audioBlobs = audioFile ? [audioFile] : [];

      await saveDraft(
        draftItems,
        inputMethod,
        recordingLanguage,
        audioBlobs.length > 0 ? audioBlobs : undefined,
        photoBlobs.length > 0 ? photoBlobs : undefined,
      );

      // Clear captured items after saving
      handleStartOver();
      toast.success(t('create.draftSaved'));
    } catch {
      toast.error(t('create.draftSaveFailed'));
    }
  }, [audioFile, photos, notes, streamingCaptured, streaming.finalizedTranscript, capturedStreamingDuration, duration, inputMethod, recordingLanguage, saveDraft, handleStartOver, t]);

  const handleRestoreDraft = useCallback(async (draftId: string) => {
    setIsRestoring(true);
    try {
      const restored = await restoreDraft(draftId);

      log.info('Restoring draft', {
        draftId,
        audioBlobs: restored.audioBlobs.length,
        photos: restored.photos.length,
        notes: restored.notes.length,
        streamingTranscripts: restored.streamingTranscripts.length,
      });

      // Clear any existing items first
      restorePhotos([]);
      restoreNotes([]);
      setAudioFile(null);
      setRestoredAudioDuration(0);
      setRestoredStreamingTranscripts([]);
      setStreamingCaptured(false);
      setCapturedStreamingDuration(0);

      // Restore first audio blob (batch recording)
      if (restored.audioBlobs.length > 0) {
        setAudioFile(restored.audioBlobs[0].blob);
        setRestoredAudioDuration(restored.audioBlobs[0].duration);
      }

      // Restore photos into CreateContext
      if (restored.photos.length > 0) {
        const capturedPhotos: CapturedPhoto[] = restored.photos.map((p) => ({
          id: crypto.randomUUID(),
          blob: p.blob,
          timestamp: p.timestamp,
          capturedAt: Date.now(),
        }));
        restorePhotos(capturedPhotos);
      }

      // Restore notes into CreateContext
      if (restored.notes.length > 0) {
        const restoredNotes: RecordingNote[] = restored.notes.map((n) => ({
          id: n.id,
          text: n.text,
          timestamp: n.timestamp,
          createdAt: Date.now(),
        }));
        restoreNotes(restoredNotes);
      }

      // Restore streaming transcripts
      if (restored.streamingTranscripts.length > 0) {
        setRestoredStreamingTranscripts(
          restored.streamingTranscripts.map((st, i) => ({
            id: `restored-${i}`,
            transcript: st.transcript,
            duration: st.duration,
          }))
        );
      }

      // Set input method from draft
      if (restored.inputMethod) {
        setInputMethod(restored.inputMethod as InputMethod);
      }

      toast.success(t('create.draftRestored'));
    } catch {
      toast.error(t('create.draftSaveFailed'));
    } finally {
      setIsRestoring(false);
    }
  }, [restoreDraft, restorePhotos, restoreNotes, t]);

  const handleDeleteDraft = useCallback(async (draftId: string) => {
    try {
      await deleteDraft(draftId);
      toast.success(t('create.draftDeleted'));
    } catch {
      toast.error(t('create.draftSaveFailed'));
    }
  }, [deleteDraft, t]);

  // Handle adding a note from text input
  const handleAddNoteFromText = useCallback(() => {
    if (!note.trim()) return;
    addNote(note.trim());
    setNote('');
    toast.success(t('create.noteCaptured'));
  }, [note, addNote, t]);

  // Handle text/document continue (temporary - for upload document flow)
  const handleTextContinue = useCallback(() => {
    if (!note.trim()) return;
    setStep('project');
  }, [note]);

  // Build recordings array — batch audio recordings only
  const recordings = useMemo((): AudioRecording[] => {
    const result: AudioRecording[] = [];

    // Add current batch recording if exists
    if (audioFile) {
      result.push({
        id: 'current',
        blob: audioFile,
        duration: restoredAudioDuration || duration,
      });
    }

    return result;
  }, [audioFile, duration, restoredAudioDuration]);

  // Build streaming transcripts array — captured streaming + interrupted sessions + restored from drafts
  const streamingTranscripts = useMemo((): CapturedStreamingTranscript[] => {
    const result: CapturedStreamingTranscript[] = [];

    // Add interrupted recordings (streaming sessions that crashed)
    interruptedRecordings.forEach((rec) => {
      result.push({
        id: rec.sessionId,
        duration: rec.duration,
        isInterrupted: true,
        minutesAgo: rec.minutesAgo,
      });
    });

    // Add captured streaming recording if exists
    if (streamingCaptured) {
      result.push({
        id: 'streaming',
        duration: capturedStreamingDuration,
        transcript: streaming.finalizedTranscript || undefined,
      });
    }

    // Add restored streaming transcripts from drafts
    restoredStreamingTranscripts.forEach((st) => {
      result.push({
        id: st.id,
        duration: st.duration,
        transcript: st.transcript,
      });
    });

    return result;
  }, [interruptedRecordings, streamingCaptured, capturedStreamingDuration, streaming.finalizedTranscript, restoredStreamingTranscripts]);

  // Handle preview recording
  const handlePreviewRecording = useCallback((recordingId: string) => {
    if (recordingId === 'current') {
      // For current recording, we'd need to create a blob URL
      // For now, just show a message
      toast.info('Preview for current recording coming soon');
    } else {
      // This is an interrupted recording
      previewInterruptedRecording(recordingId);
    }
  }, [previewInterruptedRecording]);

  // Handle discard batch recording
  const handleDiscardRecording = useCallback((recordingId: string) => {
    if (recordingId === 'current') {
      setAudioFile(null);
      removeUpload('audio');
      setUploadedS3Key('');
      setUploadId('');
      toast.success(t('create.recordingDiscarded'));
    }
  }, [t, removeUpload]);

  // Handle removing a note (also cleans up upload state)
  const handleRemoveNote = useCallback((noteId: string) => {
    removeNote(noteId);
    removeUpload(noteId);
    uploadedNoteIdsRef.current.delete(noteId);
    toast.success(t('create.noteRemoved'));
  }, [removeNote, removeUpload, t]);

  // Handle discard streaming transcript (captured, interrupted, or restored from draft)
  const handleDiscardStreamingTranscript = useCallback((transcriptId: string) => {
    if (transcriptId === 'streaming') {
      streaming.discardStreaming();
      setStreamingCaptured(false);
      setCapturedStreamingDuration(0);
      toast.success(t('create.streamingTranscriptDiscarded'));
    } else if (transcriptId.startsWith('restored-')) {
      // Restored from draft
      setRestoredStreamingTranscripts(prev => prev.filter(st => st.id !== transcriptId));
      toast.success(t('create.streamingTranscriptDiscarded'));
    } else {
      // Interrupted recording
      handleDiscardInterruptedRecording(transcriptId);
    }
  }, [handleDiscardInterruptedRecording, streaming, t]);

  // Handle removing a photo (also cleans up upload state)
  const handleRemovePhoto = useCallback((photoId: string) => {
    removePhoto(photoId);
    removeUpload(photoId);
    uploadedPhotoIdsRef.current.delete(photoId);
    toast.success(t('create.photoRemoved'));
  }, [removePhoto, removeUpload, t]);

  // Handle retry of a failed upload
  const handleRetryUpload = useCallback((id: string) => {
    if (id === 'audio' && audioFile) {
      const ct = audioFile.type || 'audio/mp4';
      const ext = ct.includes('webm') ? 'webm' : 'mp4';
      removeUpload('audio');
      uploadBlob('audio', audioFile, `recording.${ext}`, ct).then((entry) => {
        if (entry) {
          setUploadedS3Key(entry.s3Key);
          setUploadId(entry.uploadId);
        }
      });
    } else {
      const photo = photos.find(p => p.id === id);
      if (photo) {
        removeUpload(id);
        uploadBlob(id, photo.blob, `photo-${id}.jpg`, 'image/jpeg');
      } else {
        const n = notes.find(n => n.id === id);
        if (n) {
          removeUpload(id);
          const blob = new Blob([n.text], { type: 'text/plain' });
          uploadBlob(id, blob, `note-${id}.txt`, 'text/plain');
        }
      }
    }
  }, [audioFile, photos, notes, removeUpload, uploadBlob]);

  // Auth loading state
  if (authLoading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
        </div>
      </PageLayout>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Mic className="w-8 h-8 text-indigo-400" />}
        title={t('create.unauthTitle')}
        description={t('create.unauthDescription')}
      />
    );
  }

  return (
    <PageLayout variant="plain">
      <div className="flex flex-col min-h-screen">

      {/* Page Header */}
      <Chrome component="appPageHeader">
        <AppPageHeader
          icon={<Sparkles className="w-4 h-4" />}
          badge={t('create.badge')}
          subtitle={t('create.subtitle')}
        />        
      </Chrome>

      {/* Main Content Section */}
      <section className="py-8 sm:py-10 bg-gradient-to-b from-slate-50 via-white to-indigo-50/30 flex-1">
        <div className="container mx-auto px-3 sm:px-6 max-w-4xl">
          {/* Progress Steps */}
          <ProgressSteps currentStep={step} onStepClick={(s) => setStep(s)} />

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Interrupted Session Recovery Banner - Streaming Mode */}
          {step === 'capture' && streaming.interruptedSessions.length > 0 && (
            <div className="mb-6">
              {streaming.interruptedSessions.map((session) => {
                const minutesAgo = Math.round((Date.now() - session.lastActiveAt) / 60000);
                const durationFormatted = `${Math.floor(session.durationSeconds / 60)}:${(session.durationSeconds % 60).toString().padStart(2, '0')}`;

                return (
                  <div
                    key={session.sessionId}
                    className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center gap-3"
                  >
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-amber-900">
                        {t('create.interruptedRecording')}
                      </p>
                      <p className="text-sm text-amber-700 mt-0.5">
                        <Clock className="w-3.5 h-3.5 inline mr-1" />
                        {t('create.interruptedDetails', {
                          minutesAgo,
                          duration: durationFormatted,
                          fragments: session.fragmentCount,
                        })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <button
                        onClick={() => streaming.resumeInterruptedSession(session.sessionId, session.languageCode)}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                      >
                        <Play className="w-3.5 h-3.5" />
                        {t('create.resumeSession')}
                      </button>
                      <button
                        onClick={() => streaming.discardStreaming()}
                        className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-white text-amber-700 border border-amber-300 rounded-lg text-sm font-medium hover:bg-amber-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {t('create.discardSession')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}


          {/* Content based on step */}
          <div>


            {/* Step 1: Input Method Selection */}
            {step === 'capture' && (
              <div>
                {/* Consolidated Tab Bar */}
                <div className="mb-1 pb-4 border-slate-200">
                  <TabsList variant="underlined">
                    <Tab
                      variant="underlined"
                      active={inputMethod === 'voice'}
                      onClick={() => setInputMethod('voice')}
                      className="justify-center flex-1"
                    >
                      <Mic className="w-4 h-4" />
                      <span className="hidden sm:inline">{t('create.tabRecord')}</span>
                    </Tab>
                    <Tab
                      variant="underlined"
                      active={inputMethod === 'photo'}
                      onClick={() => setInputMethod('photo')}
                      className="justify-center flex-1"
                    >
                      <Image className="w-4 h-4" />
                      <span className="hidden sm:inline">Photo</span>
                    </Tab>
                    <Tab
                      variant="underlined"
                      active={inputMethod === 'upload-recording' || inputMethod === 'upload-document'}
                      onClick={() => setInputMethod('upload-recording')}
                      className="justify-center flex-1"
                    >
                      <Upload className="w-4 h-4" />
                      <span className="hidden sm:inline">Upload</span>
                    </Tab>
                    <Tab
                      variant="underlined"
                      active={inputMethod === 'text'}
                      onClick={() => setInputMethod('text')}
                      className="justify-center flex-1"
                    >
                      <ClipboardPaste className="w-4 h-4" />
                      <span className="hidden sm:inline">Text</span>
                    </Tab>
                  </TabsList>
                </div>

                {/* Upload Sub-Options (when Upload tab is active) */}
                {(inputMethod === 'upload-recording' || inputMethod === 'upload-document') && (
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <button
                      onClick={() => setInputMethod('upload-recording')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        inputMethod === 'upload-recording'
                          ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      Audio File
                    </button>
                    <button
                      onClick={() => setInputMethod('upload-document')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        inputMethod === 'upload-document'
                          ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <FileUp className="w-4 h-4" />
                      Document
                    </button>
                  </div>
                )}

                {/* Tab Content */}
                {inputMethod === 'voice' && (
                  <AudioRecorder
                    recordingLanguage={recordingLanguage}
                    onStreamingSubmit={handleStreamingCapture}
                    streamingCaptured={streamingCaptured}
                  />
                )}

                {inputMethod === 'photo' && (
                  <PhotoCapturePanel />
                )}

                {inputMethod === 'upload-recording' && (
                  <div>
                    <FileUploader onFileSelected={handleFileSelected} />
                    {uploadedAudioFile && (
                      <div className="mt-6 text-center">
                        <p className="text-sm text-slate-600 mb-4">
                          {uploadedAudioFile instanceof File ? uploadedAudioFile.name : 'Audio file loaded'}
                        </p>
                        <Button
                          onClick={handleCaptureAudio}
                          variant="gradient"
                          size="lg"
                          shape="rounded"
                        >
                          {t('create.captureAudio')}
                          <ArrowRight className="w-5 h-5" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {inputMethod === 'upload-document' && (
                  <DocumentUploadPanel
                    note={note}
                    onNoteChange={setNote}
                    onContinue={handleTextContinue}
                    onCapture={handleCaptureDocument}
                  />
                )}

                {inputMethod === 'text' && (
                  <TextInputPanel
                    note={note}
                    onNoteChange={setNote}
                    onAddNote={handleAddNoteFromText}
                  />
                )}

                {/* Captured Items Panel - Show summary of all captured content */}
                <div className="mt-6">
                  <CapturedItemsPanel
                    recordings={recordings}
                    streamingTranscripts={streamingTranscripts}
                    photos={photos}
                    notes={notes}
                    uploadStatuses={uploads}
                    onPreviewRecording={handlePreviewRecording}
                    onDiscardRecording={handleDiscardRecording}
                    onDiscardStreamingTranscript={handleDiscardStreamingTranscript}
                    onRetryUpload={handleRetryUpload}
                    previewingRecordingId={previewSessionId}
                    previewBlobUrl={previewBlobUrl}
                    previewLoading={previewLoading}
                    onStopPreview={stopPreview}
                    onRemoveNote={handleRemoveNote}
                    onRemovePhoto={handleRemovePhoto}
                  />
                </div>

                {/* My Drafts Panel */}
                {draftsEnabled && (
                  <MyDraftsPanel
                    drafts={drafts}
                    isLoading={draftsLoading}
                    hasAttemptedLoad={draftsHasAttemptedLoad}
                    onRestore={handleRestoreDraft}
                    onDelete={handleDeleteDraft}
                    isRestoring={isRestoring}
                  />
                )}

                {/* Global Action Buttons - Submit All and Save Draft */}
                {(recordings.length > 0 || streamingTranscripts.length > 0 || photos.length > 0 || notes.length > 0 || (inputMethod === 'upload-document' && note.trim())) && (
                  <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                    {draftsEnabled && (
                      <Button
                        variant="outline"
                        size="lg"
                        shape="rounded"
                        onClick={handleSaveDraft}
                        disabled={draftSaving}
                        className="flex-1 sm:flex-none"
                      >
                        {draftSaving ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Clock className="w-5 h-5" />
                        )}
                        {draftSaving ? t('create.savingDraft') : t('create.saveDraft')}
                      </Button>
                    )}
                    <Button
                      variant="gradient"
                      size="lg"
                      shape="rounded"
                      disabled={anyUploading}
                      onClick={() => {
                        const hasItems = audioFile || streamingCaptured || (inputMethod === 'text' ? notes.length > 0 : note.trim());
                        if (!hasItems) {
                          toast.error(t('create.nothingToSubmit'));
                          return;
                        }
                        const submitError = validateBeforeSubmit();
                        if (submitError) {
                          toast.error(t('create.noInternetConnection'));
                          return;
                        }
                        if (anyUploading) {
                          toast.info(t('create.uploadsInProgress'));
                          return;
                        }
                        if (anyFailed) {
                          toast.error(t('create.uploadsFailed'));
                          return;
                        }
                        setStep('project');
                      }}
                      className="flex-1 sm:flex-none"
                    >
                      {anyUploading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('create.uploading')}
                        </>
                      ) : (
                        <>
                          {t('create.selectProject', 'Select Project')}
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Project Selection */}
            {step === 'project' && hasContent && (
              <div className="py-4">
                {/* Creating project overlay */}
                {isCreatingProject && (
                  <div className="text-center py-12">
                    <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
                    <p className="font-medium text-slate-900">{t('create.creatingProject')}</p>
                  </div>
                )}

                {!isCreatingProject && (
                  <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('create.selectProject')}
                  </h2>
                  <p className="text-slate-600">
                    {t('create.selectProjectDescription')}
                  </p>
                </div>

                {/* Search / Create Project */}
                <div className="relative mb-4">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={projectSearchQuery}
                    onChange={(e) => setProjectSearchQuery(e.target.value)}
                    placeholder={t('create.searchOrCreateProject')}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                  />
                </div>

                {(() => {
                  const query = projectSearchQuery.trim().toLowerCase();
                  const filteredProjects = query
                    ? projects.filter((p) => p.projectName.toLowerCase().includes(query))
                    : projects;
                  const exactMatch = query
                    ? projects.some((p) => p.projectName.toLowerCase() === query)
                    : true;

                  const colorClasses: Record<string, string> = {
                    blue: 'border-blue-200 hover:border-blue-400 bg-blue-50',
                    purple: 'border-purple-200 hover:border-purple-400 bg-purple-50',
                    emerald: 'border-emerald-200 hover:border-emerald-400 bg-emerald-50',
                    amber: 'border-amber-200 hover:border-amber-400 bg-amber-50',
                    slate: 'border-slate-200 hover:border-slate-400 bg-slate-50',
                    indigo: 'border-indigo-200 hover:border-indigo-400 bg-indigo-50',
                  };

                  return (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {/* Create option when no exact match */}
                      {query && !exactMatch && (
                        <button
                          onClick={() => handleCreateProject(projectSearchQuery.trim())}
                          className="w-full p-4 rounded-xl border-2 border-dashed border-indigo-300
                                   text-left transition-all hover:shadow-md hover:border-indigo-400 hover:bg-indigo-50"
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-2 rounded-lg bg-indigo-50">
                              <Plus className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-indigo-700">
                                {t('create.createProjectNamed', { name: projectSearchQuery.trim() })}
                              </h3>
                            </div>
                            <ArrowRight className="w-5 h-5 text-indigo-400" />
                          </div>
                        </button>
                      )}

                      {/* Filtered project list */}
                      {filteredProjects.map((project) => {
                        const meta = project.type
                          ? projectTypeMetadata[project.type]
                          : projectTypeMetadata['other'];
                        const Icon = meta.icon;
                        const templateCount = (project.templateIds || []).length;

                        return (
                          <button
                            key={project.id}
                            onClick={() => handleProjectSelect(project)}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${colorClasses[meta.color] || colorClasses.slate}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="p-2 rounded-lg bg-white/50">
                                <Icon className="w-5 h-5 text-slate-500" />
                              </div>
                              <div className="flex-1">
                                <h3 className="font-semibold text-slate-900">{project.projectName}</h3>
                                <p className="text-sm text-slate-600 mt-0.5">
                                  {templateCount > 0
                                    ? t('create.documentTypesAssigned', { count: templateCount })
                                    : t('create.noDocumentTypes')}
                                </p>
                              </div>
                              <ArrowRight className="w-5 h-5 text-slate-400" />
                            </div>
                          </button>
                        );
                      })}

                      {/* Loading state */}
                      {filteredProjects.length === 0 && !query && projectsLoading && (
                        <div className="text-center py-8 text-slate-500">
                          <Loader2 className="w-8 h-8 mx-auto mb-3 text-indigo-500 animate-spin" />
                          <p>{t('create.loadingProjects')}</p>
                        </div>
                      )}

                      {/* Empty state */}
                      {filteredProjects.length === 0 && !query && !projectsLoading && (
                        <div className="text-center py-8 text-slate-500">
                          <FolderOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>{t('create.noProjectsYet')}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
                  </>
                )}
              </div>
            )}

            {/* Step 4: Template Selection (multi-select with checkboxes) */}
            {step === 'template' && hasContent && selectedProject && (
              <div className="py-4">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    {t('create.selectDocumentTypes')}
                  </h2>
                  <p className="text-slate-600">
                    {t('create.selectDocumentTypesFor')} <span className="font-medium">{selectedProject.projectName}</span>
                  </p>
                  {selectedTemplates.length > 0 && (
                    <p className="text-sm text-indigo-600 mt-2">
                      {t('create.selectedCount', { count: selectedTemplates.length })}
                    </p>
                  )}
                </div>

                {/* Inline template cards */}
                {projectTemplatesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <Loader2 className="w-7 h-7 animate-spin text-indigo-600 mx-auto mb-3" />
                      <p className="text-sm text-slate-600">{t('create.loadingTemplates')}</p>
                    </div>
                  </div>
                ) : projectTemplates.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {projectTemplates.map((template) => {
                      const isSelected = selectedTemplates.some(t => t.id === template.id);
                      const colorMap: Record<string, { bg: string; text: string }> = {
                        blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
                        emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
                        purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
                        amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
                        rose: { bg: 'bg-rose-100', text: 'text-rose-600' },
                        cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600' },
                        indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                        slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
                      };
                      const colors = colorMap[template.color || 'slate'] || colorMap.slate;

                      return (
                        <button
                          key={template.id}
                          onClick={() => handleTemplateSelect(template)}
                          className={`
                            w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left
                            transition-all duration-200 hover:shadow-md hover:scale-[1.005]
                            ${isSelected
                              ? 'border-indigo-500 bg-indigo-50 shadow-md'
                              : 'border-slate-200 hover:border-indigo-300'
                            }
                          `}
                        >
                          {/* Checkbox */}
                          <div className={`
                            w-5 h-5 rounded border-2 flex items-center justify-center shrink-0
                            ${isSelected
                              ? 'bg-indigo-600 border-indigo-600'
                              : 'bg-white border-slate-300'
                            }
                          `}>
                            {isSelected && (
                              <Check className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>

                          <div className={`p-2.5 rounded-lg ${colors.bg} shrink-0`}>
                            <FileText className={`w-5 h-5 ${colors.text}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-slate-900 truncate">{template.name}</h4>
                            {template.description && (
                              <p className="text-sm text-slate-500 truncate mt-0.5">{template.description}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-600 mb-1">{t('create.noDocTypesAssigned')}</p>
                    <p className="text-sm text-slate-500">{t('create.browseTemplates')}</p>
                  </div>
                )}

                {/* Add document type */}
                <div className="mt-4 flex items-center justify-center">
                  <button
                    onClick={() => setShowTemplatePicker(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-50 rounded-lg font-medium transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('create.addDocumentType')}
                  </button>
                </div>

                {/* Generate Documents Button */}
                {selectedTemplates.length > 0 && (
                  <div className="mt-6 flex justify-center">
                    <Button
                      onClick={handleSubmitMultipleJobs}
                      variant="gradient"
                      size="lg"
                      shape="rounded"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('create.submitting')}
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          {selectedTemplates.length === 1
                            ? t('create.generateDocument')
                            : t('create.generateDocuments', { count: selectedTemplates.length })}
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </Button>
                  </div>
                )}

              </div>
            )}

            {/* Submitting overlay (shown while auto-submitting after template selection) */}
            {isSubmitting && step === 'template' && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
                <div className="text-center">
                  <Loader2 className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-3" />
                  <p className="font-medium text-slate-900">{t('create.submitting')}</p>
                  <p className="text-sm text-slate-500">{t('create.submittingReadyTime')}</p>
                </div>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* Template Picker Modal */}
      <TemplatePickerModal
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onTemplateAdded={addTemplateToProject}
        projectId={selectedProject?.id}
      />

      </div>
    </PageLayout>
  );
};

export default AddPage;
